export type AvailabilityProblem = {
  type?: string;
  status?: number;
  detail?: string;
  requestId?: string;
};
export type AvailabilityErrorView = {
  root: HTMLElement;
  panel: HTMLElement | null;
  error: unknown;
  translate: (key: string, cs: string, en: string) => string;
  problemMessage: (error: AvailabilityProblem) => string;
  cooldownSeconds: number;
  getContext: () => string;
  isActive: (context: string) => boolean;
  onRetry: () => Promise<void>;
};

export function normalizeAvailabilityProblem(
  value: unknown,
): AvailabilityProblem {
  const raw =
    value && typeof value === "object"
      ? (value as Record<string, unknown>)
      : {};
  const problem: AvailabilityProblem = {
    type: typeof raw.type === "string" ? raw.type : undefined,
    status: typeof raw.status === "number" ? raw.status : undefined,
    detail: typeof raw.detail === "string" ? raw.detail : undefined,
    requestId: typeof raw.requestId === "string" ? raw.requestId : undefined,
  };

  return problem;
}

/** Keep the reserved geometry while the time panel owns the error announcement. */
export function clearAvailabilityFailureStatus(root: ParentNode): void {
  const notice = root.querySelector<HTMLElement>(".availability-status");
  if (
    !notice ||
    notice.classList.contains("reschedule-day-error") ||
    notice.querySelector("[data-reschedule-day-retry]")
  )
    return;
  notice.setAttribute("role", "status");
  notice.setAttribute("aria-hidden", "true");
  notice.classList.remove("is-busy");
  const label = notice.querySelector("span");
  if (label) label.textContent = "";
  notice.querySelectorAll("button").forEach((button) => button.remove());
}

/** Presentation only: the owner retains request/Abort and routing authority. */
export function mountAvailabilityFailure(view: AvailabilityErrorView): void {
  const app = view.root;
  const document = app.ownerDocument;
  const availabilityFailure = view.error;
  const ui = view.translate;
  const problemMessage = view.problemMessage;
  const availabilityRetrySeconds = () => view.cooldownSeconds;
  const panel = view.panel;
  if (!panel || !panel.isConnected || !app.contains(panel)) return;
  const failure = document.createElement("section");
  failure.className = "availability-failure";
  failure.setAttribute("role", "alert");
  const message = document.createElement("p");
  const problem = normalizeAvailabilityProblem(availabilityFailure);
  const providerFailure = [
    "calendar-provider-unavailable",
    "booking-calendar-unavailable",
  ].includes(problem?.type?.split("/").pop() ?? "");
  message.textContent =
    problem.status === 429
      ? ui(
          "calendar.rateLimited",
          "Dostupnost ověřujeme příliš často. Po uplynutí čekání ji automaticky obnovíme.",
          "Availability is being checked too often. We will retry automatically after the waiting period.",
        )
      : providerFailure
        ? ui(
            "calendar.providerUnavailable",
            "Kalendář je dočasně nedostupný. Dostupnost se nepodařilo ověřit.",
            "The calendar provider is temporarily unavailable. Availability could not be verified.",
          )
        : problemMessage(problem);
  failure.append(message);
  if (problem.status !== 429) {
    const automaticRetry = document.createElement("p");
    automaticRetry.textContent = ui(
      "calendar.automaticRetry",
      "Automaticky to zkusíme znovu.",
      "We will retry automatically.",
    );
    failure.append(automaticRetry);
  }
  const requestId = problem.requestId;
  if (
    typeof requestId === "string" &&
    /^[a-zA-Z0-9_-]{1,128}$/.test(requestId)
  ) {
    const reference = document.createElement("p");
    reference.textContent = `${ui("error.requestReference", "Kód požadavku", "Request ID")}: ${requestId}`;
    failure.append(reference);
  }
  const retry = document.createElement("button");
  retry.id = "availability-retry";
  retry.type = "button";
  retry.className = "text-button";
  retry.textContent = ui(
    "calendar.retryVerification",
    "Zkusit ověřit dostupnost",
    "Retry availability verification",
  );
  const cooldown = availabilityRetrySeconds();
  retry.disabled = cooldown > 0;
  if (cooldown > 0) {
    const waiting = document.createElement("p");
    waiting.className = "availability-retry-wait";
    waiting.textContent = ui(
      "calendar.retryWait",
      "Opakování bude dostupné po uplynutí čekání vyžadovaného službou.",
      "Retry will be available after the service's required waiting period.",
    );
    failure.append(waiting);
    window.setTimeout(() => {
      if (retry.isConnected) {
        retry.disabled = false;
        waiting.remove();
      }
    }, cooldown * 1000);
  }
  retry.onclick = async () => {
    const restoreFocus = document.activeElement === retry;
    const context = view.getContext();
    await view.onRetry();
    if (
      restoreFocus &&
      view.isActive(context) &&
      document.activeElement === document.body
    ) {
      const next =
        app.querySelector<HTMLElement>("#availability-retry:not([disabled])") ??
        app.querySelector<HTMLElement>(".day-slots .slot:not([disabled])") ??
        app.querySelector<HTMLElement>(".availability-failure p") ??
        app.querySelector<HTMLElement>(".day-slots h3");
      if (next) {
        if (next.tagName === "H3" || next.tagName === "P") next.tabIndex = -1;
        next.focus({ preventScroll: true });
      }
    }
  };
  failure.append(retry);
  panel.querySelector(".slot-list")?.remove();
  panel.append(failure);
  clearAvailabilityFailureStatus(app);
}
